import { useState, useEffect, useCallback } from 'react';
import { getBundles, setBundleFeatures } from './api';
import { BUNDLES, defaultBundleFeatures } from './featureAccess';
import { invalidateBundleFeatures } from './useBundleFeatures';

const sameSet = (a, b) => a.length === b.length && a.every((k) => b.includes(k));

// Global bundle catalog editor (bundle → features), wired to the backend.
// GET /api/v3/bundles → { features, bundles:[{key,label,features,customized}] }.
export default function useBundles() {
    const [catalog, setCatalog] = useState([]);
    const [bundleKey, setBundleKey] = useState(null);
    const [selected, setSelected] = useState([]);
    const [saved, setSaved] = useState([]);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await getBundles();
            const list = (res && res.data && res.data.bundles) || [];
            setCatalog(list);
            setBundleKey((prev) => prev || (list[0] && list[0].key) || null);
        } catch {
            setCatalog([]);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    // Sync the editor to the selected bundle's current (effective) features.
    useEffect(() => {
        const b = catalog.find((x) => x.key === bundleKey);
        if (!b) { setSelected([]); setSaved([]); return; }
        setSelected(b.features || []);
        setSaved(b.features || []);
    }, [bundleKey, catalog]);

    const dirty = selected.length !== saved.length || selected.some((k) => !saved.includes(k));
    // "customized" reflects the live selection vs. the bundle's config default —
    // so it flips back to "default" the moment the selection matches the default
    // again, whether saved or not.
    const customized = !!bundleKey && !sameSet(selected, defaultBundleFeatures(bundleKey));
    const isEmpty = selected.length === 0;
    const canSave = dirty && !isEmpty;

    const save = useCallback(async () => {
        if (!bundleKey || selected.length === 0) return;
        setSaving(true);
        try {
            const res = await setBundleFeatures(bundleKey, selected);
            const keys = (res && res.data && res.data.feature_keys) || selected;
            setSaved(keys);
            setSelected(keys);
            setCatalog((prev) => prev.map((b) => (b.key === bundleKey ? { ...b, features: keys, customized: true } : b)));
            // Other screens (Licenses card, per-user Access modal) read this
            // catalog through a module-level cache — drop it so they re-fetch.
            invalidateBundleFeatures();
        } finally {
            setSaving(false);
        }
    }, [bundleKey, selected]);

    const reset = useCallback(() => setSelected(saved), [saved]);

    // Empty override resets the bundle to its config default on next read.
    const restoreDefault = useCallback(async () => {
        if (!bundleKey) return;
        setSaving(true);
        try {
            await setBundleFeatures(bundleKey, []);
            invalidateBundleFeatures();
            const res = await getBundles();
            const list = (res && res.data && res.data.bundles) || [];
            setCatalog(list);
            const b = list.find((x) => x.key === bundleKey);
            if (b) { setSelected(b.features || []); setSaved(b.features || []); }
        } finally {
            setSaving(false);
        }
    }, [bundleKey]);

    return {
        bundles: catalog.length ? catalog : BUNDLES,
        bundleKey, setBundleKey, selected, setSelected, saved, dirty, saving, customized, isEmpty, canSave, save, reset, restoreDefault,
    };
}
